import { isRecord } from '../core/json';
import type { LibraryFile } from './library';
import type { Opened } from './open';

export interface DemoProgress {
  readonly loaded: number;
  readonly total: number;
}

type Fetcher = (url: URL) => Promise<Response>;

interface DemoFile {
  readonly path: string;
  readonly size: number;
}

type StreamedResponse = Response & { readonly body: ReadableStream<Uint8Array<ArrayBuffer>> };

const isDemoFile = (value: unknown): value is DemoFile =>
  isRecord(value) && typeof value['path'] === 'string' && Number.isSafeInteger(value['size']);

const hasBody = (response: Response): response is StreamedResponse => response.body !== null;

function parseDemo(json: unknown): readonly DemoFile[] {
  const files = isRecord(json) ? json['files'] : undefined;
  if (!Array.isArray(files) || files.length === 0 || !files.every(isDemoFile)) throw new Error('its file list is unreadable');
  return files;
}

async function fetchOk(fetcher: Fetcher, url: URL): Promise<StreamedResponse> {
  const response = await fetcher(url);
  if (!response.ok || !hasBody(response)) throw new Error(`${url.pathname} answered ${response.status}`);
  return response;
}

async function download(fetcher: Fetcher, url: URL, onBytes: (bytes: number) => void): Promise<Blob> {
  const response = await fetchOk(fetcher, url);
  const reader = response.body.getReader();
  const chunks: Uint8Array<ArrayBuffer>[] = [];
  for (let chunk = await reader.read(); !chunk.done; chunk = await reader.read()) {
    chunks.push(chunk.value);
    onBytes(chunk.value.byteLength);
  }
  return new Blob(chunks, { type: response.headers.get('Content-Type') ?? '' });
}

export async function openedFromDemo(list: URL, onProgress: (progress: DemoProgress) => void, fetcher: Fetcher = fetch): Promise<Opened> {
  const files = parseDemo(await (await fetchOk(fetcher, list)).json());
  const total = files.reduce((sum, file) => sum + file.size, 0);
  let loaded = 0;
  const count = (bytes: number): void => {
    loaded += bytes;
    onProgress({ loaded, total });
  };
  const downloaded = await Promise.all(
    files.map(async ({ path }): Promise<LibraryFile> => {
      const file = await download(fetcher, new URL(path, list), count);
      return { path, size: file.size, lastModified: 0, file };
    }),
  );
  return { name: files[0]?.path.split('/')[0] ?? 'Demo', files: downloaded, handle: undefined };
}

export const demoProgressText = ({ loaded, total }: DemoProgress): string =>
  `Loading the demo · ${Math.min(100, Math.floor((loaded / Math.max(1, total)) * 100))}%`;
